import {
  FETCH_ALL_SESSIONS,
  FETCH_ALL_SESSIONS_START,
  FETCH_TAGS,
  FETCH_SESSIONS_PAGE_START,
  FETCH_SESSIONS_PAGE_SUCCESS,
  FETCH_SESSIONS_PAGE_FAILURE,
  FETCH_SESSIONS_GROUP_START,
  FETCH_SESSIONS_GROUP_SUCCESS,
  FETCH_SESSIONS_GROUP_FAILURE,
} from "../constants/ActionTypes";

const PARENT_TYPES = ['category', 'subcategory', 'audiobook', 'series', 'challenge', 'unparented'];

const emptyGroup = () => ({
  data: [],
  page: 1,
  per_page: 25,
  page_count: 0,
  has_more: false,
  total_count: 0,
  loading: false,
  error: null,
});

const INITIAL_STATE = {
  // Legacy state (one-row-per-parent-join). Still used by anything reading
  // `state.sessions.all_sessions` directly. Untouched.
  isLoading: false,
  all_sessions: null,
  current_tags: null,

  // Paginated state (one-row-per-session) — populated by `fetchSessionsPage`.
  paginated: {
    data: [],
    page: 1,
    per_page: 50,
    page_count: 0,
    has_more: false,
    total_count: 0,
  },
  paginatedLoading: false,
  paginatedError: null,

  // Grouped-view state — one slice per parent_type, populated by
  // `fetchSessionsGroup(type)`. Each slice mirrors the paginated envelope
  // so loading / has_more / total_count are tracked per group.
  grouped: Object.fromEntries(PARENT_TYPES.map((t) => [t, emptyGroup()])),
};

export default function (state = INITIAL_STATE, action) {
  switch (action.type) {
    case FETCH_ALL_SESSIONS_START:
      return { ...state, isLoading: true };
    case FETCH_ALL_SESSIONS:
      return { ...state, all_sessions: action.payload, isLoading: false };
    case FETCH_TAGS:
      return { ...state, current_tags: action.payload };

    case FETCH_SESSIONS_PAGE_START:
      return { ...state, paginatedLoading: true, paginatedError: null };
    case FETCH_SESSIONS_PAGE_SUCCESS:
      return {
        ...state,
        paginatedLoading: false,
        paginated: {
          data:        action.payload.data        ?? [],
          page:        action.payload.page        ?? 1,
          per_page:    action.payload.per_page    ?? 50,
          page_count:  action.payload.page_count  ?? 0,
          has_more:    !!action.payload.has_more,
          total_count: action.payload.total_count ?? 0,
        },
      };
    case FETCH_SESSIONS_PAGE_FAILURE:
      return { ...state, paginatedLoading: false, paginatedError: action.payload };

    case FETCH_SESSIONS_GROUP_START: {
      const { parent_type } = action.payload;
      const prev = state.grouped[parent_type] || emptyGroup();
      return {
        ...state,
        grouped: {
          ...state.grouped,
          [parent_type]: { ...prev, loading: true, error: null },
        },
      };
    }
    case FETCH_SESSIONS_GROUP_SUCCESS: {
      const { parent_type, response } = action.payload;
      const prev = state.grouped[parent_type] || emptyGroup();
      // Pages > 1 are "load more" actions — append to the existing data so
      // the user sees rows accumulate inside the section. Page 1 replaces.
      const incoming = response.data ?? [];
      const nextData = (response.page ?? 1) > 1
        ? [...prev.data, ...incoming]
        : incoming;
      return {
        ...state,
        grouped: {
          ...state.grouped,
          [parent_type]: {
            data:        nextData,
            page:        response.page        ?? 1,
            per_page:    response.per_page    ?? 25,
            page_count:  response.page_count  ?? 0,
            has_more:    !!response.has_more,
            total_count: response.total_count ?? 0,
            loading: false,
            error: null,
          },
        },
      };
    }
    case FETCH_SESSIONS_GROUP_FAILURE: {
      const { parent_type, error } = action.payload;
      const prev = state.grouped[parent_type] || emptyGroup();
      return {
        ...state,
        grouped: {
          ...state.grouped,
          [parent_type]: { ...prev, loading: false, error },
        },
      };
    }

    default:
      return state;
  }
}
